import { useState, useEffect, useRef } from "react";
import { useAuth } from "../_core/hooks/useAuth";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { TwoFactorAuthSettings } from "../components/TwoFactorAuthSettings";
import {
  ArrowLeft,
  Settings as SettingsIcon,
  Package,
  Tag,
  Droplet,
  CreditCard,
  Cloud,
  Mail,
  Globe,
  Building2,
  Scale,
  Lock,
  Upload,
  Shield,
  Key,
  Smartphone
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PrintFormatsManager } from "@/components/PrintFormatsManager";
import { PackagesManager } from "@/components/PackagesManager";
import { LegalPagesManager } from "@/components/LegalPagesManager";
import { ShippingRatesManager } from "@/components/ShippingRatesManager";
import { StorageMonitoring } from "@/components/StorageMonitoring";
import { StorageSettingsForm } from "@/components/StorageSettingsForm";
import { GlobalR2ConfigForm } from "@/components/GlobalR2ConfigForm";
import { AllUsersStorageTable } from "@/components/AllUsersStorageTable";
import { useLocation } from "wouter";
import React from "react";
import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES, type SupportedCurrency } from "@shared/const";

const currencyLabels: Record<SupportedCurrency, string> = {
  EUR: "EUR (€)",
  USD: "USD ($)",
};

export default function Settings() {
  const { user } = useAuth();
  const { t, language, setLanguage } = useTranslation();
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();

  const { data: settings, refetch, isLoading, error } = trpc.settings.get.useQuery();
  
  const updatePricingMutation = trpc.settings.updatePricing.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || t.settings.settingsFailed || "Fehler beim Speichern der Einstellungen");
    },
  });
  const updateWatermarkMutation = trpc.settings.updateWatermark.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved);
      refetch();
    },
  });
  const updatePayPalMutation = trpc.settings.updatePayPal.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved);
      refetch();
    },
  });
  const updateSMTPMutation = trpc.settings.updateSMTP.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved);
      refetch();
    },
  });
  const testSMTPMutation = trpc.settings.testSMTP.useMutation({
    onSuccess: (res) => {
      if (res.success) {
        toast.success(`${t.settings.smtpTestSuccess} (${res.latencyMs}ms)`);
      } else {
        toast.error(`${t.settings.smtpTestFailed}: ${res.error}`);
      }
    },
    onError: (e) => toast.error(e.message),
  });
  const updateSuperPasswordMutation = trpc.settings.updateSuperPassword.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved);
      setShowSuperPasswordDialog(false);
      setCurrentSuperPassword("");
      setNewSuperPassword("");
      setConfirmSuperPassword("");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || t.settings.settingsFailed || "Fehler beim Speichern des Super-Passworts");
    },
  });
  const verifySuperPasswordMutation = trpc.settings.verifySuperPassword.useMutation();
  const updateLocalizationMutation = trpc.settings.updateLocalization.useMutation({
    onSuccess: () => {
      toast.success(t.settings.settingsSaved);
      refetch();
    },
  });

  const [pricingForm, setPricingForm] = useState({
    pricePerImage: "5.00",
    pricePerVideo: "10.00",
    pricePrint10x15: "5.00",
    pricePrint13x18: "7.50",
    pricePrint15x20: "10.00",
    pricePrint20x30: "15.00",
    currency: DEFAULT_CURRENCY as SupportedCurrency,
    companyTaxRate: "19.00",
  });

  const [watermarkForm, setWatermarkForm] = useState({
    watermarkEnabled: true,
    watermarkText: "",
    watermarkPosition: "center" as any,
    watermarkOpacity: 50,
  });

  const [paypalForm, setPaypalForm] = useState({
    paypalMode: "sandbox" as any,
    paypalClientId: "",
    paypalClientSecret: "",
  });

  // Password protection states (hash is never sent to client — verify server-side only)
  const [showPayPalPasswordDialog, setShowPayPalPasswordDialog] = useState(false);
  const [paypalPassword, setPaypalPassword] = useState("");
  const [paypalUnlocked, setPaypalUnlocked] = useState(false);

  // Super password management
  const [showSuperPasswordDialog, setShowSuperPasswordDialog] = useState(false);
  const [currentSuperPassword, setCurrentSuperPassword] = useState("");
  const [newSuperPassword, setNewSuperPassword] = useState("");
  const [confirmSuperPassword, setConfirmSuperPassword] = useState("");

  const [smtpForm, setSmtpForm] = useState({
    smtpHost: "",
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: "",
    smtpPassword: "",
    smtpFrom: "",
    emailNotificationsEnabled: true,
    notificationEmail: "",
    sendInvoiceEmail: true,
  });
  const [smtpTestRecipient, setSmtpTestRecipient] = useState("");

  const [localizationForm, setLocalizationForm] = useState({
    language: "bs",
    timezone: "Europe/Sarajevo",
    dateFormat: "DD.MM.YYYY",
  });

  const [companyForm, setCompanyForm] = useState({
    companyName: "",
    companyAddressLine1: "",
    companyAddressLine2: "",
    companyPostalCode: "",
    companyCity: "",
    companyCountry: "",
    companyVatId: "",
    companyTaxNumber: "",
    companyEmail: "",
    companyPhone: "",
    companyWebsite: "",
    companyBankName: "",
    companyIban: "",
    companySwift: "",
    companyInvoiceFooter: "",
    companyTaxRate: "",
  });

  const [systemForm, setSystemForm] = useState({
    maxCodeLogins: 3,
    downloadLinkDays: 10,
    maxManualDigitalImages: 10,
  });

  const globalSystemName = settings?.systemName || "Okulus";
  const globalBaseUrl =
    settings?.baseUrl ||
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:5173");

  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");
  const [logoFileName, setLogoFileName] = useState<string>("");
  const logoFileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadCompanyLogoMutation = trpc.settings.uploadCompanyLogo.useMutation({
    onSuccess: (res) => {
      setLogoPreviewUrl(res.url);
      // Refresh sidebar/logo wherever it's used
      utils.settings.getCompanyLogo.invalidate();
      toast.success(t.settings.settingsSaved);
    },
    onError: (e) => toast.error(e.message),
  });

  const handleCompanyLogoChange = (file?: File) => {
    if (!file) return;
    setLogoFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setLogoPreviewUrl(result);
      uploadCompanyLogoMutation.mutate({ dataUrl: result });
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };
  const [iconPreviewUrl, setIconPreviewUrl] = useState<string>("");
  const [iconFileName, setIconFileName] = useState<string>("");
  const iconFileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadSystemIconMutation = trpc.settings.uploadSystemIcon.useMutation({
    onSuccess: (res) => {
      setIconPreviewUrl(res.url);
      // Ensure global favicon query refreshes for immediate update
      utils.settings.getSystemIcon.invalidate();
      toast.success("System-Icon hochgeladen");
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSystemIconChange = (file?: File) => {
    if (!file) return;
    setIconFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setIconPreviewUrl(result);
      uploadSystemIconMutation.mutate({ dataUrl: result });
      if (iconFileInputRef.current) {
        iconFileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };


  const updateCompanyMutation = trpc.settings.updateCompany.useMutation({
    onSuccess: () => { toast.success(t.settings.settingsSaved); refetch(); },
    onError: (e) => { toast.error(e.message); },
  });

  const updateSystemMutation = trpc.settings.updateSystem.useMutation({
    onSuccess: () => { toast.success(t.settings.settingsSaved); refetch(); },
    onError: (e) => { toast.error(e.message); },
  });

  useEffect(() => {
    if (settings) {
      setPricingForm({
        pricePerImage: settings.pricePerImage || "5.00",
        pricePerVideo: (settings as any).pricePerVideo || "10.00",
        pricePrint10x15: (settings as any).pricePrint10x15 || "5.00",
        pricePrint13x18: (settings as any).pricePrint13x18 || "7.50",
        pricePrint15x20: (settings as any).pricePrint15x20 || "10.00",
        pricePrint20x30: (settings as any).pricePrint20x30 || "15.00",
        currency: (settings.currency || DEFAULT_CURRENCY) as SupportedCurrency,
        companyTaxRate: (settings as any).companyTaxRate || "19.00",
      });
      setWatermarkForm({
        watermarkEnabled: settings.watermarkEnabled || false,
        watermarkText: settings.watermarkText || "",
        watermarkPosition: settings.watermarkPosition || "center",
        watermarkOpacity: settings.watermarkOpacity || 50,
      });
      setPaypalForm({
        paypalMode: settings.paypalMode || "sandbox",
        paypalClientId: settings.paypalClientId || "",
        paypalClientSecret: settings.paypalClientSecret || "",
      });
      setSmtpForm({
        smtpHost: settings.smtpHost || "",
        smtpPort: settings.smtpPort || 587,
        smtpSecure: settings.smtpSecure || false,
        smtpUser: settings.smtpUser || "",
        smtpPassword: settings.smtpPassword || "",
        smtpFrom: settings.smtpFrom || "",
        emailNotificationsEnabled: settings.emailNotificationsEnabled !== false,
        notificationEmail: settings.notificationEmail || "",
        sendInvoiceEmail: (settings as any).sendInvoiceEmail !== false,
      });
      setLocalizationForm({
        language: language || settings.language || "bs",
        timezone: settings.timezone || "Europe/Sarajevo",
        dateFormat: (settings as any).dateFormat || "DD.MM.YYYY",
      });
      setCompanyForm({
        companyName: (settings as any).companyName || "",
        companyAddressLine1: (settings as any).companyAddressLine1 || "",
        companyAddressLine2: (settings as any).companyAddressLine2 || "",
        companyPostalCode: (settings as any).companyPostalCode || "",
        companyCity: (settings as any).companyCity || "",
        companyCountry: (settings as any).companyCountry || "",
        companyVatId: (settings as any).companyVatId || "",
        companyTaxNumber: (settings as any).companyTaxNumber || "",
        companyEmail: (settings as any).companyEmail || "",
        companyPhone: (settings as any).companyPhone || "",
        companyWebsite: (settings as any).companyWebsite || "",
        companyBankName: (settings as any).companyBankName || "",
        companyIban: (settings as any).companyIban || "",
        companySwift: (settings as any).companySwift || "",
        companyInvoiceFooter: (settings as any).companyInvoiceFooter || "",
        companyTaxRate: (settings as any).companyTaxRate || "",
      });
      setSystemForm({
        maxCodeLogins: (settings as any).maxCodeLogins || 3,
        downloadLinkDays: (settings as any).downloadLinkDays || 10,
        maxManualDigitalImages: (settings as any).maxManualDigitalImages || 10,
      });
      const existingLogoKey = (settings as any).companyLogoGcsKey;
      if (existingLogoKey) {
        const fileName = existingLogoKey.split("/").pop() || existingLogoKey;
        setLogoFileName(fileName);
      } else {
        setLogoFileName("");
      }
    }
  }, [settings, language]);

  if (!user) {
    return <div>{t.settings.pleaseLogin}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900">
      <div className="container mx-auto p-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          className="mb-6 gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.common.back}
        </Button>

        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-3">
              <SettingsIcon className="h-10 w-10 text-purple-600" />
              {t.settings.title}
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">
              {t.settings.manageDescription}
            </p>
          </div>
          <ThemeToggle />
        </div>

        <Tabs defaultValue="system" className="space-y-6">
          <TabsList className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-purple-200 dark:border-purple-800">
            <TabsTrigger value="system">
              <SettingsIcon className="h-4 w-4 mr-2" />
              {t.settings.system || "System"}
            </TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="h-4 w-4 mr-2" />
              {t.settings.security || "Sicherheit"}
            </TabsTrigger>
            <TabsTrigger value="packages">
              <Package className="h-4 w-4 mr-2" />
              {t.packages.title}
            </TabsTrigger>
            <TabsTrigger value="pricing">
              <Tag className="h-4 w-4 mr-2" />
              {t.settings.pricing}
            </TabsTrigger>
            <TabsTrigger value="shipping">
              <Package className="h-4 w-4 mr-2" />
              {t.settings.shippingRates}
            </TabsTrigger>
            <TabsTrigger value="watermark">
              <Droplet className="h-4 w-4 mr-2" />
              {t.settings.watermark}
            </TabsTrigger>
            <TabsTrigger value="paypal">
              <CreditCard className="h-4 w-4 mr-2" />
              {t.settings.paypal}
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="storage">
                <Cloud className="h-4 w-4 mr-2" />
                {t.settings.storage}
              </TabsTrigger>
            )}
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              {t.settings.notifications}
            </TabsTrigger>
            <TabsTrigger value="localization">
              <Globe className="h-4 w-4 mr-2" />
              {t.settings.localization}
            </TabsTrigger>
            <TabsTrigger value="company">
              <Building2 className="h-4 w-4 mr-2" />
              {t.settings.companyInfo}
            </TabsTrigger>
            <TabsTrigger value="legal">
              <Scale className="h-4 w-4 mr-2" />
              {t.settings.legal || "Legal"}
            </TabsTrigger>
          </TabsList>

          {/* Security Settings */}
          <TabsContent value="security">
            <TwoFactorAuthSettings />
          </TabsContent>

          {/* System Settings */}
          <TabsContent value="system">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
              <CardHeader>
                <CardTitle>{t.settings.system || "System"}</CardTitle>
                <CardDescription>
                  {t.settings.systemDescription || "Systemname und Basis-URL konfigurieren"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <Label htmlFor="systemName">{t.settings.systemName || "Systemname"}</Label>
                    <Input
                      id="systemName"
                      type="text"
                      value={globalSystemName}
                      readOnly
                      disabled
                      className="bg-muted text-muted-foreground"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t.settings.systemNameGlobalNote || "Dieser Wert ist global und kann nur zentral geändert werden."}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="baseUrl">{t.settings.baseUrl || "Basis-URL"}</Label>
                    <Input
                      id="baseUrl"
                      type="text"
                      value={globalBaseUrl}
                      readOnly
                      disabled
                      className="bg-muted text-muted-foreground"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t.settings.baseUrlHelp || "Die Basis-URL wird für QR-Codes und Links verwendet"}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="maxCodeLogins">{t.settings.codeLoginLimit || "Max. Logins pro Code"}</Label>
                  <Input
                    id="maxCodeLogins"
                    type="number"
                    min={1}
                    max={100}
                    value={systemForm.maxCodeLogins}
                    onChange={(e) => setSystemForm({ ...systemForm, maxCodeLogins: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t.settings.codeLoginLimitHelp || "Wie oft ein Zugangscode (PDF) benutzt werden darf"}
                  </p>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="downloadLinkDays">{t.settings.downloadLinkDays || "Download-Link Gültigkeit (Tage)"}</Label>
                  <Input
                    id="downloadLinkDays"
                    type="number"
                    min={1}
                    max={30}
                    value={systemForm.downloadLinkDays}
                    onChange={(e) => setSystemForm({ ...systemForm, downloadLinkDays: parseInt(e.target.value) || 10 })}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t.settings.downloadLinkDaysHelp || "Wie lange Kunden den ZIP-Download nutzen können"}
                  </p>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="maxManualDigitalImages">{t.settings.maxManualDigitalImages || "Max. digitale Bilder (manuell)"}</Label>
                  <Input
                    id="maxManualDigitalImages"
                    type="number"
                    min={1}
                    max={100}
                    value={systemForm.maxManualDigitalImages}
                    onChange={(e) => setSystemForm({ ...systemForm, maxManualDigitalImages: parseInt(e.target.value) || 10 })}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t.settings.maxManualDigitalImagesHelp || "Maximale Anzahl digitaler Bilder pro Login-Code (ohne Paket)"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      updateSystemMutation.mutate({
                        maxCodeLogins: systemForm.maxCodeLogins || 3,
                        downloadLinkDays: systemForm.downloadLinkDays || 10,
                        maxManualDigitalImages: systemForm.maxManualDigitalImages || 10,
                      });
                    }}
                    disabled={updateSystemMutation.isPending}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {t.settings.save}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowSuperPasswordDialog(true)}
                    className="gap-2"
                  >
                    <Lock className="h-4 w-4" />
                    {t.settings.changeSuperPassword || "Super-Passwort ändern"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages */}
          <TabsContent value="packages">
            <PackagesManager />
          </TabsContent>

          {/* Legal Pages */}
          <TabsContent value="legal">
            <LegalPagesManager />
          </TabsContent>

          {/* Pricing Settings */}
          <TabsContent value="pricing">
            <div className="space-y-6">
              {/* Digital Price */}
              <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
                <CardHeader>
                  <CardTitle>{t?.settings?.pricing || "Preise"}</CardTitle>
                  <CardDescription>
                    {t?.settings?.pricingDescription || "Konfigurieren Sie Preise für Bilder"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label htmlFor="pricePerImage">{t?.settings?.pricePerImage || "Preis pro Bild"} (Digital)</Label>
                    <Input
                      id="pricePerImage"
                      type="text"
                      value={pricingForm.pricePerImage}
                      onChange={(e) =>
                        setPricingForm({ ...pricingForm, pricePerImage: e.target.value })
                      }
                      placeholder={t?.settings?.placeholders?.pricePerImage || "5.00"}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="pricePerVideo">{t?.settings?.pricePerVideo || "Preis pro Video"}</Label>
                    <Input
                      id="pricePerVideo"
                      type="text"
                      value={pricingForm.pricePerVideo}
                      onChange={(e) =>
                        setPricingForm({ ...pricingForm, pricePerVideo: e.target.value })
                      }
                      placeholder={t?.settings?.placeholders?.pricePerVideo || "z.B. 10.00"}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="currency">{t?.settings?.currency || "Währung"}</Label>
                    <Select
                      value={pricingForm.currency}
                      onValueChange={(value) =>
                        setPricingForm({ ...pricingForm, currency: value as SupportedCurrency })
                      }
                    >
                      <SelectTrigger id="currency">
                        <SelectValue placeholder={DEFAULT_CURRENCY} />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_CURRENCIES.map((code) => (
                          <SelectItem key={code} value={code}>
                            {currencyLabels[code as SupportedCurrency]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyTaxRate">{t?.settings?.companyTaxRate || "Steuersatz (%)"}</Label>
                    <Input
                      id="companyTaxRate"
                      type="text"
                      value={pricingForm.companyTaxRate}
                      onChange={(e) =>
                        setPricingForm({ ...pricingForm, companyTaxRate: e.target.value })
                      }
                      placeholder="19.00"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const payload = { ...pricingForm };
                      
                      // Helper function to format prices to XX.XX format
                      const formatPrice = (value: string): string => {
                        if (!value) return "0.00";
                        let v = value.replace(',', '.');
                        // If no decimal point, add .00
                        if (!/\./.test(v)) v += '.00';
                        // If only one decimal place, add 0
                        else if (/\.\d$/.test(v)) v += '0';
                        return v;
                      };
                      
                      // Format all price fields
                      payload.pricePerImage = formatPrice(payload.pricePerImage);
                      payload.pricePerVideo = formatPrice(payload.pricePerVideo);
                      payload.pricePrint10x15 = formatPrice(payload.pricePrint10x15);
                      payload.pricePrint13x18 = formatPrice(payload.pricePrint13x18);
                      payload.pricePrint15x20 = formatPrice(payload.pricePrint15x20);
                      payload.pricePrint20x30 = formatPrice(payload.pricePrint20x30);
                      payload.companyTaxRate = formatPrice(payload.companyTaxRate);
                      
                      // Validate format before sending
                      const priceRegex = /^\d+\.\d{2}$/;
                      const requiredFields = ['pricePerImage'];
                      const allPriceFields = ['pricePerImage', 'pricePerVideo', 'pricePrint10x15', 'pricePrint13x18', 'pricePrint15x20', 'pricePrint20x30', 'companyTaxRate'];
                      
                      // Check required fields
                      for (const field of requiredFields) {
                        if (!priceRegex.test(payload[field as keyof typeof payload] as string)) {
                          toast.error(`${field}: Ungültiges Format (erwartet: X.XX)`);
                          return;
                        }
                      }
                      
                      // Check optional fields if they have values
                      for (const field of allPriceFields) {
                        const value = payload[field as keyof typeof payload] as string;
                        if (value && !priceRegex.test(value)) {
                          toast.error(`${field}: Ungültiges Format (erwartet: X.XX)`);
                          return;
                        }
                      }
                      
                      updatePricingMutation.mutate(payload);
                    }}
                    disabled={updatePricingMutation.isPending}
                  >
                    {t?.common?.save || "Speichern"}
                  </Button>
                </CardContent>
              </Card>

              {/* Print Formats Manager */}
              <PrintFormatsManager />
            </div>
          </TabsContent>

          {/* Shipping Rates */}
          <TabsContent value="shipping">
            <ShippingRatesManager currency={pricingForm.currency} />
          </TabsContent>

          {/* Company / Invoicing Settings */}
          <TabsContent value="company">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardHeader>
                <CardTitle>{t.settings.companyInfo}</CardTitle>
                <CardDescription>{t.settings.companyInfoDescription}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="companyName">{t.settings.companyName}</Label>
                    <Input id="companyName" value={companyForm.companyName} onChange={e=>setCompanyForm({...companyForm,companyName:e.target.value})} placeholder={t.settings.placeholders.companyName} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyVatId">{t.settings.companyVatId}</Label>
                    <Input id="companyVatId" value={companyForm.companyVatId} onChange={e=>setCompanyForm({...companyForm,companyVatId:e.target.value})} placeholder={t.settings.placeholders.companyVatId} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyTaxNumber">{t.settings.companyTaxNumber}</Label>
                    <Input id="companyTaxNumber" value={companyForm.companyTaxNumber} onChange={e=>setCompanyForm({...companyForm,companyTaxNumber:e.target.value})} placeholder={t.settings.placeholders.companyTaxNumber} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyTaxRate">{t.settings.companyTaxRate}</Label>
                    <Input id="companyTaxRate" value={companyForm.companyTaxRate} onChange={e=>setCompanyForm({...companyForm,companyTaxRate:e.target.value})} placeholder={t.settings.placeholders.companyTaxRate} />
                  </div>
                  <div className="space-y-3 md:col-span-2">
                    <Label htmlFor="companyAddressLine1">{t.settings.companyAddressLine1}</Label>
                    <Input id="companyAddressLine1" value={companyForm.companyAddressLine1} onChange={e=>setCompanyForm({...companyForm,companyAddressLine1:e.target.value})} placeholder={t.settings.placeholders.companyAddressLine1} />
                  </div>
                  <div className="space-y-3 md:col-span-2">
                    <Label htmlFor="companyAddressLine2">{t.settings.companyAddressLine2}</Label>
                    <Input id="companyAddressLine2" value={companyForm.companyAddressLine2} onChange={e=>setCompanyForm({...companyForm,companyAddressLine2:e.target.value})} placeholder={t.settings.placeholders.companyAddressLine2} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyPostalCode">{t.settings.companyPostalCode}</Label>
                    <Input id="companyPostalCode" value={companyForm.companyPostalCode} onChange={e=>setCompanyForm({...companyForm,companyPostalCode:e.target.value})} placeholder={t.settings.placeholders.companyPostalCode} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyCity">{t.settings.companyCity}</Label>
                    <Input id="companyCity" value={companyForm.companyCity} onChange={e=>setCompanyForm({...companyForm,companyCity:e.target.value})} placeholder={t.settings.placeholders.companyCity} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyCountry">{t.settings.companyCountry}</Label>
                    <Input id="companyCountry" value={companyForm.companyCountry} onChange={e=>setCompanyForm({...companyForm,companyCountry:e.target.value})} placeholder={t.settings.placeholders.companyCountry} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyEmail">{t.settings.companyEmail}</Label>
                    <Input id="companyEmail" type="email" value={companyForm.companyEmail} onChange={e=>setCompanyForm({...companyForm,companyEmail:e.target.value})} placeholder={t.settings.placeholders.companyEmail} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyPhone">{t.settings.companyPhone}</Label>
                    <Input id="companyPhone" value={companyForm.companyPhone} onChange={e=>setCompanyForm({...companyForm,companyPhone:e.target.value})} placeholder={t.settings.placeholders.companyPhone} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyWebsite">{t.settings.companyWebsite}</Label>
                    <Input id="companyWebsite" value={companyForm.companyWebsite} onChange={e=>setCompanyForm({...companyForm,companyWebsite:e.target.value})} placeholder={t.settings.placeholders.companyWebsite} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyBankName">{t.settings.companyBankName}</Label>
                    <Input id="companyBankName" value={companyForm.companyBankName} onChange={e=>setCompanyForm({...companyForm,companyBankName:e.target.value})} placeholder={t.settings.placeholders.companyBankName} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companyIban">{t.settings.companyIban}</Label>
                    <Input id="companyIban" value={companyForm.companyIban} onChange={e=>setCompanyForm({...companyForm,companyIban:e.target.value})} placeholder={t.settings.placeholders.companyIban} />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="companySwift">{t.settings.companySwift}</Label>
                    <Input id="companySwift" value={companyForm.companySwift} onChange={e=>setCompanyForm({...companyForm,companySwift:e.target.value})} placeholder={t.settings.placeholders.companySwift} />
                  </div>
                  <div className="space-y-3 md:col-span-2">
                    <Label>{t.settings.companyLogoUploadLabel}</Label>
                    <input
                      ref={logoFileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleCompanyLogoChange(e.target.files?.[0])}
                    />
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Button
                        type="button"
                        variant="default"
                        className="w-fit gap-2"
                        onClick={() => logoFileInputRef.current?.click()}
                        disabled={uploadCompanyLogoMutation.isPending}
                      >
                        <Upload className="h-4 w-4" />
                        {uploadCompanyLogoMutation.isPending
                          ? t.common.loading
                          : (t.settings.companyLogoUploadButton || "Logo hochladen")}
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {logoFileName || t.settings.companyLogoNoFile || "Kein Logo ausgewählt"}
                      </span>
                    </div>
                    {logoPreviewUrl && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">{t.settings.companyLogoPreview}</p>
                        <img src={logoPreviewUrl} alt="Company Logo Preview" className="max-h-32 rounded border" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 md:col-span-2">
                    <Label htmlFor="companyInvoiceFooter">{t.settings.companyInvoiceFooter}</Label>
                    <Textarea id="companyInvoiceFooter" value={companyForm.companyInvoiceFooter} onChange={e=>setCompanyForm({...companyForm,companyInvoiceFooter:e.target.value})} placeholder={t.settings.placeholders.companyInvoiceFooter} />
                  </div>
                  {user?.role === 'admin' && (
                    <div className="space-y-3 md:col-span-2">
                      <Label>System-Icon (Favicon)</Label>
                      <p className="text-sm text-muted-foreground">Icon für Browser-Tabs und Lesezeichen. Empfohlen: PNG/ICO, 32×32px oder 64×64px.</p>
                      <input
                        ref={iconFileInputRef}
                        type="file"
                        accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => handleSystemIconChange(e.target.files?.[0])}
                      />
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Button
                          type="button"
                          variant="default"
                          className="w-fit gap-2"
                          onClick={() => iconFileInputRef.current?.click()}
                          disabled={uploadSystemIconMutation.isPending}
                        >
                          <Upload className="h-4 w-4" />
                          {uploadSystemIconMutation.isPending ? t.common.loading : "Icon hochladen"}
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {iconFileName || "Kein Icon ausgewählt"}
                        </span>
                      </div>
                      {iconPreviewUrl && (
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">Vorschau</p>
                          <img src={iconPreviewUrl} alt="System Icon Preview" className="h-16 w-16 rounded border" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Button onClick={()=>{
                  const payload = { ...companyForm };
                  // Trim inputs
                  payload.companyEmail = payload.companyEmail.trim();
                  payload.companyWebsite = payload.companyWebsite.trim();
                  // No email validation (user requested ohne Validierung)
                  if (payload.companyWebsite && !/^https?:\/\//i.test(payload.companyWebsite)) {
                    payload.companyWebsite = 'https://' + payload.companyWebsite;
                  }
                  if (payload.companyTaxRate) {
                    let v = payload.companyTaxRate.replace(',', '.');
                    if (/^\d{1,3}$/.test(v)) v += '.00';
                    else if (/^\d{1,3}\.\d$/.test(v)) v += '0';
                    payload.companyTaxRate = v;
                  }
                  updateCompanyMutation.mutate(payload);
                }} disabled={updateCompanyMutation.isPending}>{t.common.save}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Watermark Settings */}
          <TabsContent value="watermark">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <CardTitle>{t.settings.watermark}</CardTitle>
              <CardDescription>
                {t.settings.watermarkDescription}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <Label htmlFor="watermarkEnabled">{t.settings.watermarkEnabled}</Label>
                <Switch
                  id="watermarkEnabled"
                  checked={watermarkForm.watermarkEnabled}
                  onCheckedChange={(checked) =>
                    setWatermarkForm({ ...watermarkForm, watermarkEnabled: checked })
                  }
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="watermarkText">{t.settings.watermarkText}</Label>
                <Input
                  id="watermarkText"
                  value={watermarkForm.watermarkText}
                  onChange={(e) =>
                    setWatermarkForm({ ...watermarkForm, watermarkText: e.target.value })
                  }
                  placeholder={t.settings.placeholders.watermarkText}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="watermarkPosition">{t.settings.watermarkPosition}</Label>
                <Select
                  value={watermarkForm.watermarkPosition}
                  onValueChange={(value: any) =>
                    setWatermarkForm({ ...watermarkForm, watermarkPosition: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="center">{t.settings.center}</SelectItem>
                    <SelectItem value="top-left">{t.settings.topLeft}</SelectItem>
                    <SelectItem value="top-right">{t.settings.topRight}</SelectItem>
                    <SelectItem value="bottom-left">{t.settings.bottomLeft}</SelectItem>
                    <SelectItem value="bottom-right">{t.settings.bottomRight}</SelectItem>
                    <SelectItem value="tiled">{t.settings.tiled}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                <Label htmlFor="watermarkOpacity">
                  {t.settings.watermarkOpacity}: {watermarkForm.watermarkOpacity}%
                </Label>
                <Input
                  id="watermarkOpacity"
                  type="range"
                  min="0"
                  max="100"
                  value={watermarkForm.watermarkOpacity}
                  onChange={(e) =>
                    setWatermarkForm({
                      ...watermarkForm,
                      watermarkOpacity: parseInt(e.target.value),
                    })
                  }
                />
              </div>
              <Button
                onClick={() => updateWatermarkMutation.mutate(watermarkForm)}
                disabled={updateWatermarkMutation.isPending}
              >
                {t.common.save}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

          {/* PayPal Settings */}
          <TabsContent value="paypal">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t.settings.paypal}</CardTitle>
                  <CardDescription>
                    {t.settings.paypalDescription}
                  </CardDescription>
                </div>
                {!paypalUnlocked && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPayPalPasswordDialog(true)}
                    className="gap-2"
                  >
                    <Lock className="h-4 w-4" />
                    {t.common.edit}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {paypalUnlocked ? (
                <>
                  <div className="space-y-3">
                    <Label htmlFor="paypalMode">{t.settings.paypalMode}</Label>
                    <Select
                      value={paypalForm.paypalMode}
                      onValueChange={(value: any) =>
                        setPaypalForm({ ...paypalForm, paypalMode: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">{t.settings.sandbox}</SelectItem>
                        <SelectItem value="live">{t.settings.live}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="paypalClientId">{t.settings.paypalClientId}</Label>
                    <Input
                      id="paypalClientId"
                      type="text"
                      value={paypalForm.paypalClientId}
                      onChange={(e) =>
                        setPaypalForm({ ...paypalForm, paypalClientId: e.target.value })
                      }
                      placeholder={t.settings.placeholders.paypalClientId}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="paypalClientSecret">{t.settings.paypalClientSecret}</Label>
                    <Input
                      id="paypalClientSecret"
                      type="password"
                      value={paypalForm.paypalClientSecret}
                      onChange={(e) =>
                        setPaypalForm({ ...paypalForm, paypalClientSecret: e.target.value })
                      }
                      placeholder={t.settings.placeholders.paypalClientSecret}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => updatePayPalMutation.mutate(paypalForm)}
                      disabled={updatePayPalMutation.isPending}
                    >
                      {t.common.save}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setPaypalUnlocked(false)}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      {t.common.cancel}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Lock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>
                    {t.settings.storageLimitPasswordDescription ||
                      "Unesite super lozinku da biste nastavili."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

          {/* Storage Settings */}
          <TabsContent value="storage" className="space-y-6">
            {user?.role === 'admin' ? (
              <>
                {/* Global R2 Configuration (Admin Only) */}
                <GlobalR2ConfigForm />
                
                {/* All Users Storage Overview */}
                <AllUsersStorageTable />
                
                {/* Storage Monitoring */}
                <StorageMonitoring />
                
                <StorageSettingsForm settings={settings} onUpdated={() => refetch()} />
              </>
            ) : (
              <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
                <CardContent className="py-8 text-center">
                  <Lock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">
                    {t.settings.storageAdminOnly || "Storage configuration is managed by administrators."}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SMTP/Email Settings */}
          <TabsContent value="email">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader>
              <CardTitle>{t.settings.emailNotifications}</CardTitle>
              <CardDescription>
                {t.settings.emailNotificationsDescription}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="emailNotificationsEnabled"
                  checked={smtpForm.emailNotificationsEnabled}
                  onCheckedChange={(checked) =>
                    setSmtpForm({ ...smtpForm, emailNotificationsEnabled: checked })
                  }
                />
                <Label htmlFor="emailNotificationsEnabled">
                  {t.settings.enableEmailNotifications}
                </Label>
              </div>

              {smtpForm.emailNotificationsEnabled && (
                <>
                  <div className="space-y-3">
                    <Label htmlFor="notificationEmail">{t.settings.notificationEmail}</Label>
                    <Input
                      id="notificationEmail"
                      type="email"
                      value={smtpForm.notificationEmail}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, notificationEmail: e.target.value })
                      }
                      placeholder={t.settings.placeholders.notificationEmail}
                    />
                  </div>
                  <div className="border-t pt-6 mt-6">
                      <h4 className="font-semibold mb-4">{t.settings.smtpSettings}</h4>
                    
                    <div className="space-y-6">
                      <div className="space-y-3">
                        <Label htmlFor="smtpHost">{t.settings.smtpHost}</Label>
                        <Input
                          id="smtpHost"
                          value={smtpForm.smtpHost}
                          onChange={(e) =>
                            setSmtpForm({ ...smtpForm, smtpHost: e.target.value })
                          }
                          placeholder={t.settings.placeholders.smtpHost}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="smtpPort">{t.settings.smtpPort}</Label>
                        <Input
                          id="smtpPort"
                          type="number"
                          value={smtpForm.smtpPort}
                          onChange={(e) =>
                            setSmtpForm({ ...smtpForm, smtpPort: parseInt(e.target.value) })
                          }
                          placeholder={t.settings.placeholders.smtpPort}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="smtpSecure"
                          checked={smtpForm.smtpSecure}
                          onCheckedChange={(checked) =>
                            setSmtpForm({ ...smtpForm, smtpSecure: checked })
                          }
                        />
                        <Label htmlFor="smtpSecure">
                          Koristi SSL/TLS (port 465)
                        </Label>
                      </div>

                      <div className="space-y-3">
                        <Label htmlFor="smtpUser">{t.settings.smtpUser}</Label>
                        <Input
                          id="smtpUser"
                          value={smtpForm.smtpUser}
                          onChange={(e) =>
                            setSmtpForm({ ...smtpForm, smtpUser: e.target.value })
                          }
                          placeholder={t.settings.placeholders.smtpUser}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="smtpPassword">{t.settings.smtpPassword}</Label>
                        <Input
                          id="smtpPassword"
                          type="password"
                          value={smtpForm.smtpPassword}
                          onChange={(e) =>
                            setSmtpForm({ ...smtpForm, smtpPassword: e.target.value })
                          }
                          placeholder={t.settings.placeholders.smtpPassword}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="smtpFrom">{t.settings.smtpFrom}</Label>
                        <Input
                          id="smtpFrom"
                          type="email"
                          value={smtpForm.smtpFrom}
                          onChange={(e) =>
                            setSmtpForm({ ...smtpForm, smtpFrom: e.target.value })
                          }
                          placeholder={t.settings.placeholders.smtpFrom}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="smtpTestRecipient">{t.settings.smtpTestRecipient}</Label>
                        <Input
                          id="smtpTestRecipient"
                          type="email"
                          value={smtpTestRecipient}
                          onChange={(e) => setSmtpTestRecipient(e.target.value)}
                          placeholder={t.settings.placeholders.smtpTestRecipient || ''}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="sendInvoiceEmail"
                          checked={smtpForm.sendInvoiceEmail}
                          onChange={(e) =>
                            setSmtpForm({ ...smtpForm, sendInvoiceEmail: e.target.checked })
                          }
                          className="h-4 w-4"
                        />
                        <Label htmlFor="sendInvoiceEmail" className="cursor-pointer">
                          {t.settings.sendInvoiceEmail || "Automatisch Rechnung per E-Mail versenden"}
                        </Label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Button
                onClick={() => updateSMTPMutation.mutate(smtpForm)}
                disabled={updateSMTPMutation.isPending}
              >
                {t.common.save}
              </Button>
              <Button
                variant="outline"
                onClick={() => testSMTPMutation.mutate({ to: smtpTestRecipient || undefined })}
                disabled={testSMTPMutation.isPending}
              >
                {testSMTPMutation.isPending ? t.common.loading : t.settings.smtpTestConnection}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

          {/* Localization Settings */}
          <TabsContent value="localization">
            <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardHeader>
                <CardTitle>{t.settings.localization}</CardTitle>
                <CardDescription>
                  {t.settings.localizationDescription || "Choose interface language"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <Label htmlFor="language">{t.settings.language}</Label>
                  <Select
                    value={localizationForm.language}
                    onValueChange={(value) => {
                      setLocalizationForm({ ...localizationForm, language: value });
                      // Update client-side language immediately
                      setLanguage(value as any);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hr">Hrvatski</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="dateFormat">{t.settings.dateFormat}</Label>
                  <Select
                    value={localizationForm.dateFormat}
                    onValueChange={(value) =>
                      setLocalizationForm({ ...localizationForm, dateFormat: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD.MM.YYYY">DD.MM.YYYY (25.11.2025)</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (11/25/2025)</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2025-11-25)</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (25/11/2025)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={() => updateLocalizationMutation.mutate(localizationForm)}
                  disabled={updateLocalizationMutation.isPending}
                >
                  {t.common.save}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* PayPal Password Dialog */}
      <Dialog open={showPayPalPasswordDialog} onOpenChange={setShowPayPalPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {t.settings.storageLimitPasswordTitle || "Potrebna je super lozinka"}
            </DialogTitle>
            <DialogDescription>
              {t.settings.storageLimitPasswordDescription || "Geben Sie das Super-Passwort ein, um PayPal-Einstellungen zu ändern."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="paypal-password">{t.settings.password || "Passwort"}</Label>
              <Input
                id="paypal-password"
                type="password"
                value={paypalPassword}
                onChange={(e) => setPaypalPassword(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    try {
                      const res = await verifySuperPasswordMutation.mutateAsync({ password: paypalPassword });
                      if (res.ok) {
                        setPaypalUnlocked(true);
                        setShowPayPalPasswordDialog(false);
                        setPaypalPassword("");
                        toast.success(t.settings.passwordCorrect || "Zugriff gewährt");
                      } else {
                        toast.error(t.settings.passwordIncorrect || "Falsches Passwort");
                        setPaypalPassword("");
                      }
                    } catch {
                      toast.error(t.settings.passwordIncorrect || "Falsches Passwort");
                      setPaypalPassword("");
                    }
                  }
                }}
                placeholder="●●●●●●●●"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPayPalPasswordDialog(false);
                setPaypalPassword("");
              }}
            >
              {t.common.cancel || "Abbrechen"}
            </Button>
            <Button
              disabled={verifySuperPasswordMutation.isPending}
              onClick={async () => {
                try {
                  const res = await verifySuperPasswordMutation.mutateAsync({ password: paypalPassword });
                  if (res.ok) {
                    setPaypalUnlocked(true);
                    setShowPayPalPasswordDialog(false);
                    setPaypalPassword("");
                    toast.success(t.settings.passwordCorrect || "Zugriff gewährt");
                  } else {
                    toast.error(t.settings.passwordIncorrect || "Falsches Passwort");
                    setPaypalPassword("");
                  }
                } catch {
                  toast.error(t.settings.passwordIncorrect || "Falsches Passwort");
                  setPaypalPassword("");
                }
              }}
            >
              {t.common.confirm || "Bestätigen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super Password Change Dialog */}
      <Dialog open={showSuperPasswordDialog} onOpenChange={setShowSuperPasswordDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {t.settings.changeSuperPassword || "Super-Passwort ändern"}
            </DialogTitle>
            <DialogDescription>
              {t.settings.changeSuperPasswordDesc || "Ändern Sie das Passwort, das zum Schutz sensibler Einstellungen verwendet wird."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-super-password">{t.settings.currentPassword || "Aktuelles Passwort"}</Label>
              <Input
                id="current-super-password"
                type="password"
                value={currentSuperPassword}
                onChange={(e) => setCurrentSuperPassword(e.target.value)}
                placeholder="Geben Sie Ihr aktuelles Passwort ein"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-super-password">{t.settings.newPassword || "Neues Passwort"}</Label>
              <Input
                id="new-super-password"
                type="password"
                value={newSuperPassword}
                onChange={(e) => setNewSuperPassword(e.target.value)}
                placeholder="Mindestens 6 Zeichen"
                minLength={6}
              />
              <p className="text-xs text-muted-foreground">
                {newSuperPassword.length > 0 ? `${newSuperPassword.length} Zeichen` : "Mindestens 6 Zeichen erforderlich"}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-super-password">{t.settings.confirmPassword || "Passwort bestätigen"}</Label>
              <Input
                id="confirm-super-password"
                type="password"
                value={confirmSuperPassword}
                onChange={(e) => setConfirmSuperPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                minLength={6}
              />
            </div>
            {newSuperPassword !== confirmSuperPassword && newSuperPassword.length > 0 && (
              <p className="text-sm text-red-500">
                {t.settings.passwordMismatch || "Passwörter stimmen nicht überein"}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSuperPasswordDialog(false);
                setCurrentSuperPassword("");
                setNewSuperPassword("");
                setConfirmSuperPassword("");
              }}
            >
              {t.common.cancel || "Abbrechen"}
            </Button>
            <Button
              onClick={() => {
                if (!currentSuperPassword) {
                  toast.error(t.settings.currentPasswordRequired || "Aktuelles Passwort erforderlich");
                  return;
                }
                if (newSuperPassword.length < 6) {
                  toast.error(t.settings.passwordTooShort || "Passwort muss mindestens 6 Zeichen lang sein");
                  return;
                }
                if (newSuperPassword !== confirmSuperPassword) {
                  toast.error(t.settings.passwordMismatch || "Passwörter stimmen nicht überein");
                  return;
                }
                updateSuperPasswordMutation.mutate({
                  currentPassword: currentSuperPassword,
                  newPassword: newSuperPassword,
                });
              }}
              disabled={
                updateSuperPasswordMutation.isPending ||
                !currentSuperPassword ||
                newSuperPassword.length < 6 ||
                newSuperPassword !== confirmSuperPassword
              }
            >
              {updateSuperPasswordMutation.isPending ? `${t.common.saving || "Speichern"}...` : (t.common.save || "Speichern")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

